import { createSignal, Show } from "solid-js";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, UserPlus } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (email: string) => Promise<void>;
}

export function InviteMemberDialog(props: InviteMemberDialogProps) {
  const { t } = useI18n();
  const [email, setEmail] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!email().trim() || isSubmitting()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await props.onInvite(email().trim());
      showToast(t(msg`Invitation sent successfully`), "success");
      setEmail("");
      props.onOpenChange(false);
    } catch (error) {
      console.error("Error inviting member:", error);
      showToast(t(msg`Error sending invitation`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <UserPlus class="h-5 w-5" />
          </div>
          <DialogTitle class="text-xl font-bold">{t(msg`Invite member`)}</DialogTitle>
        </div>
      </DialogHeader>

      <form onSubmit={(e) => void handleSubmit(e)} class="p-6 space-y-6">
        <DialogDescription class="text-sm text-muted-foreground">
          {t(msg`Send an email invitation for someone to join your organization.`)}
        </DialogDescription>

        <div class="space-y-2">
          <Label for="email" class="text-xs uppercase tracking-wider font-bold opacity-50">
            {t(msg`Email address`)}
          </Label>
          <div class="relative">
            <Mail class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <TextField
              id="email"
              type="email"
              value={email()}
              onInput={(e) => setEmail(e.currentTarget.value)}
              placeholder={t(msg`example@email.com`)}
              autofocus
              class="pl-10 h-12 bg-muted/50 border-border/50 focus-visible:ring-primary rounded-xl"
            />
          </div>
        </div>

        <div class="flex items-center gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="ghost"
            class="h-11 px-6 rounded-xl"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting() || !email().trim()}
            class="h-11 px-8 font-bold gap-2 rounded-xl"
          >
            <Show when={isSubmitting()}>
              <div class="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            </Show>
            {t(msg`Send invitation`)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
